/**
 * Weeb Nation Theme JavaScript - v5.0 COMPLETE FIX
 * ✅ Fixes duplicate add to cart issue
 * ✅ Fixes button state loop (Adding... → Added... → Adding...)
 * ✅ Fixes cart drawer display issues
 * ✅ Adds click debouncing to prevent double-adds
 * ✅ Single source of truth for cart operations
 */

(function() {
  'use strict';

  // ============================================
  // INITIALIZATION GUARD
  // ============================================
  
  let isInitialized = false;
  const processingButtons = new Set(); // Track buttons currently processing

  // ============================================
  // SALE COUNTDOWN - SHARED LOGIC
  // ============================================
  // Single source of truth for the days/hours/minutes/seconds remaining and
  // the "is urgent" (<1hr) check. Used by snippets/sale-countdown.liquid and
  // the announcement bar's rotating sale message so both stay in sync.

  window.WeebSaleCountdown = {
    URGENT_THRESHOLD_MS: 60 * 60 * 1000, // 1 hour

    getRemaining: function(endDate) {
      const diff = endDate - Date.now();

      if (diff <= 0) {
        return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0, isUrgent: false };
      }

      return {
        expired: false,
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        isUrgent: diff < this.URGENT_THRESHOLD_MS
      };
    }
  };

  // ============================================
  // SHARED SCROLL HANDLER
  // ============================================
  // One rAF-throttled 'scroll' listener for the whole theme. Features that
  // need to react to scroll (back-to-top, the sticky header, etc.) register
  // via WeebScroll.onScroll() instead of adding their own 'scroll' listener —
  // duplicate scroll/cart listeners have caused real bugs in this theme before.

  const scrollCallbacks = [];
  let scrollTicking = false;

  function runScrollCallbacks() {
    scrollTicking = false;
    const scrollY = window.pageYOffset;
    scrollCallbacks.forEach(function(callback) {
      try {
        callback(scrollY);
      } catch (error) {
        console.error('❌ Scroll callback error:', error);
      }
    });
  }

  function handleWindowScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(runScrollCallbacks);
  }

  window.WeebScroll = {
    onScroll: function(callback) {
      scrollCallbacks.push(callback);
    }
  };

  function initializeScrollHandling() {
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    // Run once immediately so callbacks reflect the real starting scroll
    // position (e.g. after a back/forward navigation that restores scroll).
    runScrollCallbacks();
  }

  function initializeTheme() {
    if (isInitialized) return;

    try {
      console.log('🚀 Initializing Weeb Nation Theme v5.0...');

      // Initialize cart system FIRST
      initializeCart();

      // Initialize other features
      initializeScrollHandling();
      initializeScrollAnimations();
      initializeBackToTop();
      initializeProductPage();

      isInitialized = true;
      console.log('✅ Theme initialized successfully');
    } catch (error) {
      console.error('❌ Theme initialization error:', error);
    }
  }

  // ============================================
  // CART DRAWER FUNCTIONALITY
  // ============================================
  
  window.openCartDrawer = function() {
    try {
      const drawer = document.getElementById('cart-drawer');
      const overlay = document.getElementById('cart-overlay');
      
      if (!drawer || !overlay) {
        console.warn('⚠️ Cart drawer elements not found');
        return;
      }
      
      drawer.style.right = '0';
      overlay.style.display = 'block';
      document.body.style.overflow = 'hidden';
    } catch (error) {
      console.error('❌ Error opening cart drawer:', error);
    }
  };

  window.closeCartDrawer = function() {
    try {
      const drawer = document.getElementById('cart-drawer');
      const overlay = document.getElementById('cart-overlay');
      
      if (!drawer || !overlay) return;
      
      drawer.style.right = '-100%';
      overlay.style.display = 'none';
      document.body.style.overflow = '';
    } catch (error) {
      console.error('❌ Error closing cart drawer:', error);
    }
  };

  // ============================================
  // ADD TO CART - FIXED WITH DEBOUNCING
  // ============================================
  
  window.addToCartAjax = function(form) {
    if (!form) {
      console.error('❌ No form provided');
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    if (!button) {
      console.error('❌ No submit button found');
      return;
    }
    
    // Prevent duplicate clicks on same button
    const buttonId = button.dataset.buttonId || Math.random().toString(36);
    button.dataset.buttonId = buttonId;
    
    if (processingButtons.has(buttonId)) {
      console.log('⏳ Button already processing, ignoring duplicate click');
      return;
    }
    
    // Mark button as processing
    processingButtons.add(buttonId);
    
    // Store original button state
    const originalText = button.textContent;
    const originalBg = button.style.background || '';
    const originalColor = button.style.color || '';
    const originalBorder = button.style.border || '';
    
    // Update button to "Adding..." state
    button.textContent = 'Adding...';
    button.disabled = true;
    button.style.opacity = '0.7';
    
    // Get form data
    const formData = new FormData(form);
    
    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      return response.json();
    })
    .then(item => {
      console.log('✅ Item added:', item);
      
      // Show "Added!" state
      button.textContent = 'Added!';
      button.style.background = '#10B981';
      button.style.color = '#fff';
      button.style.border = 'none';
      button.style.opacity = '1';
      
      // Fetch updated cart and reload drawer
      return fetch('/cart.js');
    })
    .then(response => response.json())
    .then(cart => {
      console.log('✅ Cart updated:', cart);
      
      // Update cart count in header
      updateHeaderCartCount(cart.item_count);
      
      // Reload cart drawer content
      return reloadCartDrawer();
    })
    .then(() => {
      // Open cart drawer after content is loaded
      setTimeout(() => {
        openCartDrawer();
      }, 200);
      
      // Reset button after 2 seconds
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.style.opacity = '1';
        button.style.background = originalBg;
        button.style.color = originalColor;
        button.style.border = originalBorder;
        
        // Remove from processing set
        processingButtons.delete(buttonId);
      }, 2000);
    })
    .catch(error => {
      console.error('❌ Add to cart error:', error);
      
      // Show error state
      button.textContent = '✗ Error';
      button.style.background = '#DC2626';
      button.style.color = '#fff';
      button.style.opacity = '1';
      
      // Reset button after 2 seconds
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.style.opacity = '1';
        button.style.background = originalBg;
        button.style.color = originalColor;
        button.style.border = originalBorder;
        
        // Remove from processing set
        processingButtons.delete(buttonId);
      }, 2000);
    });
  };

  // ============================================
  // RELOAD CART DRAWER
  // ============================================
  
  function reloadCartDrawer() {
    console.log('🔄 Reloading cart drawer...');
    
    return fetch('/cart?view=drawer')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        return response.text();
      })
      .then(html => {
        const currentDrawer = document.getElementById('cart-drawer');
        
        if (!currentDrawer) {
          console.warn('⚠️ Cart drawer not found');
          return;
        }
        
        // Create temporary container
        const temp = document.createElement('div');
        temp.innerHTML = html;
        
        // Find new cart drawer content
        const newDrawer = temp.querySelector('#cart-drawer');
        
        if (!newDrawer) {
          console.warn('⚠️ New cart drawer content not found');
          return;
        }
        
        // Save current state
        const wasOpen = currentDrawer.style.right === '0px' || currentDrawer.style.right === '0';
        
        // Replace content
        currentDrawer.innerHTML = newDrawer.innerHTML;
        
        // Restore state
        if (wasOpen) {
          currentDrawer.style.right = '0';
        } else {
          currentDrawer.style.right = '-100%';
        }
        
        console.log('✅ Cart drawer reloaded');
      })
      .catch(error => {
        console.error('❌ Error reloading cart drawer:', error);
      });
  }
  
  // ============================================
  // EXPOSE reloadCartDrawer GLOBALLY
  // ============================================
  // This allows other scripts (like sticky mobile bar) to refresh the cart drawer
  window.reloadCartDrawer = reloadCartDrawer;

  // ============================================
  // UPDATE CART QUANTITY
  // ============================================
  
  window.updateCartQuantityAjax = function(key, quantity) {
    if (quantity < 1) {
      removeCartItemAjax(key);
      return;
    }

    const cartItem = document.querySelector(`[data-key="${key}"]`);
    if (cartItem) {
      cartItem.style.opacity = '0.6';
      cartItem.style.pointerEvents = 'none';
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    })
    .then(response => {
      if (!response.ok) throw new Error('Failed to update');
      return response.json();
    })
    .then(cart => {
      console.log('✅ Quantity updated');
      updateHeaderCartCount(cart.item_count);
      return reloadCartDrawer();
    })
    .catch(error => {
      console.error('❌ Update error:', error);
      if (cartItem) {
        cartItem.style.opacity = '1';
        cartItem.style.pointerEvents = 'auto';
      }
    });
  };

  // ============================================
  // REMOVE CART ITEM
  // ============================================
  
  window.removeCartItemAjax = function(key) {
    const cartItem = document.querySelector(`[data-key="${key}"]`);
    if (cartItem) {
      cartItem.style.opacity = '0.3';
      cartItem.style.pointerEvents = 'none';
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity: 0
      })
    })
    .then(response => {
      if (!response.ok) throw new Error('Failed to remove');
      return response.json();
    })
    .then(cart => {
      console.log('✅ Item removed');
      updateHeaderCartCount(cart.item_count);
      return reloadCartDrawer();
    })
    .catch(error => {
      console.error('❌ Remove error:', error);
      if (cartItem) {
        cartItem.style.opacity = '1';
        cartItem.style.pointerEvents = 'auto';
      }
    });
  };

  // ============================================
  // UPDATE HEADER CART COUNT
  // ============================================
  
  function updateHeaderCartCount(count) {
    const badges = document.querySelectorAll('.cart-count, #cart-drawer-count');
    badges.forEach(badge => {
      badge.textContent = count;
    });
  }

  // ============================================
  // INITIALIZE CART - SINGLE EVENT LISTENER
  // ============================================
  
  function initializeCart() {
    try {
      console.log('🛒 Initializing cart system...');
      
      // Wait for DOM to be fully ready
      setTimeout(() => {
        const cartForms = document.querySelectorAll('form[action="/cart/add"]');
        
        if (cartForms.length === 0) {
          console.log('ℹ️ No cart forms found on this page');
          return;
        }
        
        cartForms.forEach(form => {
          // Check if already initialized
          if (form.dataset.cartInitialized === 'true') {
            console.log('⭕ Form already initialized, skipping');
            return;
          }
          
          // Mark as initialized
          form.dataset.cartInitialized = 'true';
          
          // Add single event listener
          form.addEventListener('submit', handleCartSubmit);
          
          console.log('✅ Cart form initialized');
        });
        
        console.log(`✅ Cart system ready - ${cartForms.length} forms initialized`);
      }, 100);
      
    } catch (error) {
      console.error('❌ Error initializing cart:', error);
    }
  }

  function handleCartSubmit(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    addToCartAjax(this);
  }

  // ============================================
  // MOBILE MENU
  // ============================================
  
  window.toggleMobileMenu = function() {
    try {
      const mobileNav = document.getElementById('mobile-nav');
      if (mobileNav) {
        mobileNav.style.display = mobileNav.style.display === 'none' ? 'block' : 'none';
      }
    } catch (error) {
      console.error('❌ Mobile menu error:', error);
    }
  };

  // ============================================
  // BACK TO TOP
  // ============================================
  
  function initializeBackToTop() {
    try {
      const backToTopBtn = document.getElementById('back-to-top');
      if (!backToTopBtn) return;

      window.WeebScroll.onScroll(function(scrollY) {
        backToTopBtn.style.display = scrollY > 300 ? 'flex' : 'none';
      });
    } catch (error) {
      console.error('❌ Back to top error:', error);
    }
  }

  // ============================================
  // SCROLL ANIMATIONS
  // ============================================
  
  function initializeScrollAnimations() {
    try {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      const elementsToAnimate = document.querySelectorAll('section, .product-card, .collection-card, .testimonial-card');
      elementsToAnimate.forEach(element => observer.observe(element));
    } catch (error) {
      console.error('❌ Scroll animation error:', error);
    }
  }

  // ============================================
  // PRODUCT PAGE FUNCTIONALITY
  // ============================================
  
  function initializeProductPage() {
    window.changeMainImage = function(src) {
      try {
        const mainImage = document.getElementById('main-product-image');
        if (mainImage) mainImage.src = src;
      } catch (error) {
        console.error('❌ Change image error:', error);
      }
    };

    window.increaseQuantity = function() {
      try {
        const input = document.getElementById('quantity');
        if (input) input.value = parseInt(input.value) + 1;
      } catch (error) {
        console.error('❌ Increase quantity error:', error);
      }
    };

    window.decreaseQuantity = function() {
      try {
        const input = document.getElementById('quantity');
        if (input && parseInt(input.value) > 1) {
          input.value = parseInt(input.value) - 1;
        }
      } catch (error) {
        console.error('❌ Decrease quantity error:', error);
      }
    };
  }

  // ============================================
  // IMAGE GALLERY LIGHTBOX
  // ============================================
  
  let currentImageIndex = 0;
  let galleryImages = [];

  window.openLightbox = function(index) {
    try {
      const lightbox = document.getElementById('gallery-lightbox');
      if (!lightbox) return;
      
      currentImageIndex = index;
      
      if (typeof window.galleryImagesData !== 'undefined') {
        galleryImages = window.galleryImagesData;
      }
      
      updateLightboxImage();
      lightbox.style.display = 'block';
      document.body.style.overflow = 'hidden';
    } catch (error) {
      console.error('❌ Lightbox error:', error);
    }
  };

  window.closeLightbox = function() {
    try {
      const lightbox = document.getElementById('gallery-lightbox');
      if (lightbox) {
        lightbox.style.display = 'none';
        document.body.style.overflow = '';
      }
    } catch (error) {
      console.error('❌ Close lightbox error:', error);
    }
  };

  window.nextImage = function() {
    if (galleryImages.length > 0) {
      currentImageIndex = (currentImageIndex + 1) % galleryImages.length;
      updateLightboxImage();
    }
  };

  window.previousImage = function() {
    if (galleryImages.length > 0) {
      currentImageIndex = (currentImageIndex - 1 + galleryImages.length) % galleryImages.length;
      updateLightboxImage();
    }
  };

  function updateLightboxImage() {
    try {
      const image = galleryImages[currentImageIndex];
      const lightboxImage = document.getElementById('lightbox-image');
      const lightboxCaption = document.getElementById('lightbox-caption');
      
      if (lightboxImage && image) {
        lightboxImage.src = image.src;
      }
      
      if (lightboxCaption && image) {
        lightboxCaption.textContent = image.caption || '';
      }
    } catch (error) {
      console.error('❌ Update lightbox error:', error);
    }
  }

  // Keyboard navigation for lightbox
  document.addEventListener('keydown', function(e) {
    try {
      const lightbox = document.getElementById('gallery-lightbox');
      if (lightbox && lightbox.style.display === 'block') {
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') previousImage();
      }
    } catch (error) {
      console.error('❌ Keyboard nav error:', error);
    }
  });

  // ============================================
  // BLOG POST - COPY LINK
  // ============================================
  
  window.copyArticleLink = function() {
    try {
      const url = window.location.href;
      
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => {
          alert('Link copied to clipboard!');
        });
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = url;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('❌ Copy link error:', error);
      alert('Failed to copy link. Please copy manually: ' + window.location.href);
    }
  };


  // ============================================
  // CART UPSELLS - ADD TO CART AJAX
  // ============================================
  
  window.addUpsellToCartDrawer = function(variantId, buttonElement) {
    console.log('[UPSELL] Adding upsell to cart:', variantId);
    console.log('[UPSELL] Current URL:', window.location.href);
    
    if (buttonElement.disabled) {
      console.log('[UPSELL] Button already processing');
      return;
    }
    
    const plusIcon = buttonElement.querySelector('.plus-icon');
    const checkIcon = buttonElement.querySelector('.check-icon');
    const spinnerIcon = buttonElement.querySelector('.spinner-icon');
    
    console.log('[UPSELL] Icons found:', {
      plusIcon: !!plusIcon,
      checkIcon: !!checkIcon,
      spinnerIcon: !!spinnerIcon
    });
    
    if (!plusIcon || !checkIcon || !spinnerIcon) {
      console.error('[UPSELL] Icon elements not found');
      return;
    }
    
    buttonElement.disabled = true;
    plusIcon.style.display = 'none';
    spinnerIcon.style.display = 'block';
    console.log('[UPSELL] Loading state shown');
    
    console.log('[UPSELL] Starting fetch to /cart/add.js');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
    .then(response => {
      console.log('[UPSELL] Response received:', response.status);
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      return response.json();
    })
    .then(data => {
      console.log('[UPSELL] Product added to cart:', data);
      
      spinnerIcon.style.display = 'none';
      checkIcon.style.display = 'block';
      buttonElement.style.background = '#10B981';
      buttonElement.style.borderColor = '#10B981';
      buttonElement.style.color = '#fff';
      
      console.log('[UPSELL] Reloading cart drawer...');
      reloadCartDrawer().then(() => {
        console.log('[UPSELL] Cart drawer reloaded successfully');
      });
      
      setTimeout(() => {
        buttonElement.disabled = false;
        checkIcon.style.display = 'none';
        plusIcon.style.display = 'block';
        buttonElement.style.background = '';
        buttonElement.style.borderColor = '';
        buttonElement.style.color = '';
        console.log('[UPSELL] Button reset');
      }, 2000);
    })
    .catch(error => {
      console.error('[UPSELL] Error adding to cart:', error);
      
      spinnerIcon.style.display = 'none';
      plusIcon.style.display = 'block';
      buttonElement.style.background = '#DC2626';
      buttonElement.style.borderColor = '#DC2626';
      buttonElement.style.color = '#fff';
      
      setTimeout(() => {
        buttonElement.disabled = false;
        buttonElement.style.background = '';
        buttonElement.style.borderColor = '';
        buttonElement.style.color = '';
      }, 2000);
    });
  };
  
  console.log('[OK] addUpsellToCartDrawer function registered in theme.js');
  // ============================================
  // INITIALIZATION - MULTIPLE METHODS
  // ============================================
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTheme);
  } else {
    initializeTheme();
  }

  window.addEventListener('load', function() {
    if (!isInitialized) {
      initializeTheme();
    }
  });

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    setTimeout(initializeTheme, 50);
  }

})();

console.log('🎮 Weeb Nation Theme v5.0 - Complete Fix Loaded');